import React, { useCallback, useEffect, useMemo, useState } from "react"
import SubsectionContainer from "../shared/SubsectionContainer"
import { Provider } from "../../../domain/Provider/schema"
import { SingleValue } from "react-select"
import { useSelector } from "react-redux"
import { getSortedValuesByKeys } from "../utils/getSortedKeysByValues"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import {
  base64ToBlob,
  blobToBase64,
  formatPhoneNumber,
} from "../../shared/utils"
import { useAppDispatch } from "../../../reduxStore/hooks"
import { useSlideOver } from "../../useSlideOver"
import {
  selectCurrentUserRoles,
  selectIsAffiliateRelations,
  UserRoles,
} from "../../../domain/Account/userSlice"
import { selectValidatedHealthAdvocateUser } from "../../shared/selectors"
import { RequestReferralModal } from "../../shared/components/RequestReferral/RequestReferralModal"
import Avatar from "./../../../assets/avatar.svg"
import { Location } from "../../../domain/Practice/schema"
import { getProviderPhotoThumbnail } from "../../../domain/Profile/loader"
import { Badge } from "../../shared/components/Badge"
import UnregisteredBadge, {
  isProviderRegistered,
  isProviderManaged,
} from "../../shared/components/UnregisteredBadge"
import ManagedBadge from "../../shared/components/ManagedBadge"
import { FileStatusDisplayIdEnum } from "../../../domain/Files/schema"
import { useLazyCmsCollectionMap } from "../../../domain/CMS/useLazyCmsCollectionMap"
import {
  getAggregateInsuranceStatus,
  getAggregateLicenseStatus,
  LiabilityStatusEnum,
  LicenseStatusEnum,
} from "../../shared/utils"
import { getLicenseStatus, isLicenseStatusWarning } from "../utils/licenseUtils"
import { getStateName, useStateMap } from "../../shared/state"
import {
  getLiabilityBadgeText,
  getLiabilityBadgeColor,
} from "../utils/bannerHelpers"
import { InlinePopover } from "./InlinePopover"
import {
  getBase64ImageFromStorage,
  saveBase64ImageWithEviction,
} from "../../shared/localStorageBase64"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { FeatureFlag } from "../../../domain/enums/featureFlags"
import { hasVirtualSessionType } from "../utils/hasVirtualSessionType"

import { LiabilityInsurance as PracticeLiabilityInsurance } from "../../../domain/Practice/schema"
import { Typography, typographyTypes } from "../../shared/components/Typography"
import Card from "../../shared/components/Card"
import { Banner } from "../../shared/components/Banner"
import { ButtonWrapper } from "../../shared/components/ButtonWrapper"
import Button from "../../shared/components/Button"
import Select from "../../shared/components/Select/Select"
import cn from "../../../utils/classes"
import { ProviderTypeDisplayId } from "../../../domain/enums/displayId.enum"
import Thumbnail from "../../shared/components/Thumbnail"
import Tag from "../../shared/components/Tag"
import IconWithCaption from "../../shared/components/IconWithCaption"
import { ProviderProfileSubSectionIds } from "../constants"
import { interpolate } from "../../../domain/CMS/utils"
import { LocationTypeDisplayId } from "../../../domain/Practice/schema"

interface PracticeInfo {
  practiceName: string
  practiceId: string
  practiceStatus: string
  authReleaseRequired?: boolean
}

interface TherapistContentProps {
  providers: Provider[] | undefined
  practiceLocations: Location[]
  practiceInfo: PracticeInfo
  practiceLiabilityInsurances?: PracticeLiabilityInsurance[]
}

type LocationOption = Location & Option<React.ReactNode>

const MAX_BIO_LENGTH = 70
const TAG_LIMIT = 4

const countWords = (text: string): number => {
  const words = text.trim().split(/\s+/)
  return words.length
}

const truncWords = (text: string, maxWords: number): string => {
  const words = text.trim().split(/\s+/)
  if (words.length <= maxWords) {
    return text
  }
  return words.slice(0, maxWords).join(" ") + "..."
}

interface TherapistCardProps {
  provider: Provider
  hasARLevelAccess: boolean
  practiceLocations: Location[]
  practiceInfo: PracticeInfo
  isUnderSupervision: boolean
  practiceLiabilityInsurances?: PracticeLiabilityInsurance[]
  isLastCard: boolean
}

const TherapistCard: React.FC<TherapistCardProps> = ({
  provider,
  hasARLevelAccess = false,
  practiceLocations = [],
  practiceInfo,
  isUnderSupervision,
  practiceLiabilityInsurances,
  isLastCard,
}) => {
  const dispatch = useAppDispatch()
  const { handleSetShowProfileSidebar } = useSlideOver()
  const [isRequestReferralModalOpen, setIsRequestReferralModalOpen] =
    useState(false)
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null,
  )
  const [thumbnail, setThumbnail] = useState<string | null>(null)

  const { cms: cmsContent, isReady: isCMSReady } = useCMSReady([
    "common",
    "publicProfileSlide",
    "providerTagsMap",
    "providerDesignationsMap",
  ])
  const commonTexts = cmsContent.common
  const publicProfileContent = cmsContent.publicProfileSlide
  const tagsMap = cmsContent.providerTagsMap
  const stateMap = useStateMap()
  const designationsMap = useLazyCmsCollectionMap(
    "designationTypes",
    "providerDesignationsMap",
  )
  const providerLicenseTypeOptions = useLazyCmsCollectionMap(
    "licenseTypes",
    "providerQualificationsLicenseTypeMap",
  )

  const userRoles = useSelector(selectCurrentUserRoles)

  const isNoVirtualLocationEnabled = useFeatureFlagEnabled(
    FeatureFlag.IsNoVirtualLocationEnabled,
  )

  const canRequestReferral = [
    UserRoles.ROLE_AFFILIATE_RELATIONS,
    UserRoles.ROLE_EAP_RESEARCH,
  ].some((role) => userRoles.includes(role))

  const hasActiveDisclosure =
    !!provider.providerDisclosures?.some(
      (disclosure) =>
        disclosure.fileId != null &&
        disclosure.envelopeId != null &&
        disclosure.archivedDateTime == null,
    ) || provider.providerTypeDisplayId === ProviderTypeDisplayId.MANAGED // Managed providers don't have or need signed disclosures

  useEffect(() => {
    async function fetchData() {
      const cachedBase64Image = getBase64ImageFromStorage(
        provider?.photoFileId ?? "",
      )
      if (!cachedBase64Image && provider?.id) {
        try {
          let photoResponse
          if (
            provider.photoFileStatusDisplayId !==
            FileStatusDisplayIdEnum.QUARANTINED
          ) {
            photoResponse = await getProviderPhotoThumbnail(provider.id)
          }
          if (photoResponse && photoResponse.status === 200) {
            const fileBlob: Blob = photoResponse.data
            const base64String = await blobToBase64(fileBlob)
            saveBase64ImageWithEviction(provider.photoFileId!, base64String)
            if (base64String) {
              setThumbnail(URL.createObjectURL(base64ToBlob(base64String)))
            }
          }
        } catch (error) {
          console.error("Error fetching photo thumbnail:", error)
        }
      } else {
        setThumbnail(cachedBase64Image)
      }
    }
    fetchData()
  }, [provider.id, provider.photoFileId, provider.photoFileStatusDisplayId])

  const handleViewProfileClick = useCallback(
    (scrollToSection?: string) => {
      dispatch(() => {
        handleSetShowProfileSidebar({
          provider: { id: provider.id ?? "", userId: provider.userId },
          scrollToSection,
        })
      })
    },
    [dispatch, handleSetShowProfileSidebar, provider.id, provider.userId],
  )

  const handleRequestReferralClick = () => {
    setSelectedLocation(referralLocations[0])
    setIsRequestReferralModalOpen(true)
  }

  const handleLocationSelect = (
    selectedOption: SingleValue<Location | null>,
  ) => {
    if (selectedOption) {
      setSelectedLocation(selectedOption)
      setIsRequestReferralModalOpen(true)
    }
  }

  const filteredPracticeLocations = practiceLocations.filter(
    (location) =>
      location.id &&
      (!isNoVirtualLocationEnabled ||
        location.locationTypeDisplayId !== LocationTypeDisplayId.VIRTUAL) &&
      provider.providerLocations.find((loc) => loc.id === location.id),
  )

  const hasVirtualSession = hasVirtualSessionType(
    provider.providerSessionTypeDisplayIds,
  )

  const virtualReferralLocation: Location | null =
    isNoVirtualLocationEnabled &&
    provider.isVirtualAttested &&
    hasVirtualSession
      ? {
          id: null,
          locationTypeDisplayId: LocationTypeDisplayId.VIRTUAL,
          line1: null,
          line2: null,
          city: null,
          stateOrTerritory: null,
          postalCode: null,
          county: null,
          fax: null,
          isPrimary: false,
          isAdaAccessible: false,
          officeRequirementsCertified: false,
          lastUpdated: null,
          phones: [],
          emails: [],
        }
      : null

  const referralLocations: Location[] = [
    ...filteredPracticeLocations,
    ...(virtualReferralLocation ? [virtualReferralLocation] : []),
  ]

  const formatLocationLabel = (location: Location): React.ReactNode => {
    const address =
      location.locationTypeDisplayId === LocationTypeDisplayId.VIRTUAL
        ? commonTexts?.virtual || ""
        : `${location.line1}, ${location.line2 && location.line2.length > 0 ? location.line2 + ", " : ""}${location.city}, ${location.stateOrTerritory} ${location.postalCode}`

    return (
      <div>
        <div>{practiceInfo.practiceName}</div>
        <Typography variant={typographyTypes.NORMAL} text={address} />
      </div>
    )
  }

  const locations = useMemo(() => {
    if (isNoVirtualLocationEnabled) {
      const locs = filteredPracticeLocations
        ?.map((matchedLocation) => matchedLocation.city)
        .filter(Boolean)
      if (provider.isVirtualAttested && hasVirtualSession) {
        locs.push(
          publicProfileContent?.atAGlanceSubsectionTelehealthLabel ?? "",
        )
      }
      return locs.join(", ")
    }

    let hasVirtualLocation: boolean = false
    const locs = filteredPracticeLocations
      ?.map((matchedLocation) => {
        if (
          matchedLocation.locationTypeDisplayId ===
          LocationTypeDisplayId.VIRTUAL
        ) {
          hasVirtualLocation = true
          return null
        } else {
          return matchedLocation.city
        }
      })
      .filter(Boolean)
    if (hasVirtualLocation) {
      locs.push(publicProfileContent?.atAGlanceSubsectionTelehealthLabel ?? "")
    }
    return locs.join(", ")
  }, [
    isNoVirtualLocationEnabled,
    filteredPracticeLocations,
    provider.isVirtualAttested,
    publicProfileContent?.atAGlanceSubsectionTelehealthLabel,
    hasVirtualSession,
  ])

  // Pre-compute provider liability and license aggregate statuses for badges
  const providerLiabilityStatus = getAggregateInsuranceStatus(
    provider.liabilityInsurances || [],
  )
  const providerLicenseStatus = getAggregateLicenseStatus(
    provider.providerLicenses || [],
  )
  const showProviderLicenseBadge =
    providerLicenseStatus === LicenseStatusEnum.EXPIRED ||
    providerLicenseStatus === LicenseStatusEnum.EXPIRING_SOON

  type BadgeItem = { id: string; severity: number; element: React.ReactNode }

  const badgeItems: BadgeItem[] = React.useMemo(() => {
    // Severity scale: higher = more severe
    const BADGE_SEVERITY = {
      red: 10,
      purple: 9,
      blue: 8,
    }

    const isManaged = isProviderManaged(provider)
    const isRegistered = isProviderRegistered(provider)

    const providerLicenses = provider.providerLicenses || []
    const first3Licenses = providerLicenses.slice(0, 3)

    const navigateToLicensesSection = () => {
      handleViewProfileClick(
        ProviderProfileSubSectionIds.PROVIDER_PROFILE_QUALIFICATIONS_SECTION,
      )
    }

    const licenseBadgeElement = showProviderLicenseBadge && (
      <InlinePopover
        position="right"
        triggerElement={
          <Badge
            text={publicProfileContent?.licenseNeedsAttentionStatusText || ""}
            color="red"
          />
        }
      >
        <Card
          isTooltipCard
          tooltipPosition="right"
          styleOverrides="p-3 w-max max-w-[250px]"
          borderStyleOverride="shadow-md"
        >
          {first3Licenses.map((license, index) => (
            <div
              className={cn(
                "flex flex-col gap-3",
                index !== 0 && "pt-3",
                index !== first3Licenses.length - 1 &&
                  "pb-3 border-solid border-0 !border-b-1/4 border-gray-200",
              )}
              key={license.id}
            >
              <Typography
                variant={typographyTypes.NORMAL}
                styleOverrides={`font-bold ${
                  isLicenseStatusWarning(license) ? "!text-aaa-error-red" : ""
                }`}
                text={getLicenseStatus(license, publicProfileContent)}
              />
              <Typography
                variant={typographyTypes.NORMAL}
                text={`${publicProfileContent?.licenseToolTipStateText ?? ""} ${getStateName(stateMap, license.stateCode ?? "")}`}
              />
              <Typography
                variant={typographyTypes.NORMAL}
                text={`${publicProfileContent?.licenseToolTipLicenseText ?? ""} ${license?.licenseNumber}`}
              />
              <Typography
                variant={typographyTypes.NORMAL}
                text={`${publicProfileContent?.licenseToolTipLicenseTypeText ?? ""} ${providerLicenseTypeOptions[license.licenseTypeDisplayId!] ?? ""}`}
              />
            </div>
          ))}
          {providerLicenses.length > 3 && (
            <ButtonWrapper
              onClick={navigateToLicensesSection}
              justification="start"
              buttonWrapperStyleType="table"
              styleOverrides="w-full mt-3 py-2 border-solid border-0 !border-t-1/4 border-gray-200"
            >
              <Typography
                variant={typographyTypes.HREF}
                styleOverrides="font-semibold text-sm text-primary-blue"
                text={interpolate(publicProfileContent?.viewAllAlertsText, {
                  number: providerLicenses.length,
                })}
              />
            </ButtonWrapper>
          )}
        </Card>
      </InlinePopover>
    )

    const liabilityBadgeElement = !practiceInfo.authReleaseRequired &&
      providerLiabilityStatus !== LiabilityStatusEnum.ACTIVE && (
        <Badge
          text={getLiabilityBadgeText(
            providerLiabilityStatus,
            publicProfileContent!,
          )}
          color={getLiabilityBadgeColor(providerLiabilityStatus)}
        />
      )

    const underSupervisionElement = isUnderSupervision && (
      <Badge
        text={commonTexts?.underSupervision ?? ""}
        color="info"
        styleOverrides="px-2.5"
      />
    )

    const disclosureRequiredElement = !hasActiveDisclosure && (
      <Badge
        text={publicProfileContent?.disclosureSignatureRequiredText ?? ""}
        color="red"
        styleOverrides="px-2.5"
      />
    )

    const items: BadgeItem[] = []

    if (licenseBadgeElement) {
      items.push({
        id: "license",
        severity: BADGE_SEVERITY.red,
        element: licenseBadgeElement,
      })
    }
    if (liabilityBadgeElement) {
      items.push({
        id: "liability",
        severity:
          BADGE_SEVERITY[getLiabilityBadgeColor(providerLiabilityStatus)],
        element: liabilityBadgeElement,
      })
    }
    if (isManaged) {
      items.push({
        id: "managed",
        severity: BADGE_SEVERITY.blue,
        element: <ManagedBadge force />,
      })
    } else if (!isRegistered) {
      items.push({
        id: "unregistered",
        severity: BADGE_SEVERITY.blue,
        element: <UnregisteredBadge providerId={provider.id} />,
      })
    }
    if (underSupervisionElement) {
      items.push({
        id: "underSupervision",
        severity: BADGE_SEVERITY.blue,
        element: underSupervisionElement,
      })
    }
    if (disclosureRequiredElement) {
      items.push({
        id: "disclosureRequired",
        severity: BADGE_SEVERITY.red,
        element: disclosureRequiredElement,
      })
    }
    return items.sort((a, b) => b.severity - a.severity)
  }, [
    provider,
    showProviderLicenseBadge,
    publicProfileContent,
    practiceInfo.authReleaseRequired,
    providerLiabilityStatus,
    isUnderSupervision,
    commonTexts?.underSupervision,
    hasActiveDisclosure,
    handleViewProfileClick,
    stateMap,
    providerLicenseTypeOptions,
  ])

  const locationOptions = referralLocations.map(
    (location) =>
      ({
        ...location,
        label: formatLocationLabel(location),
      }) as LocationOption,
  )

  return (
    <div>
      {!provider.isPublicViewable && (
        <div className="pb-5">
          <Banner
            type="info"
            title={publicProfileContent?.privateProfileBannerText}
            isOpen
            sticky={false}
            content={""}
            id={`${provider.firstName}-${provider.lastName}-therapist-card`}
            styleOverrides="shadow-none"
            contentStyleOverrides="text-base"
            titleStyleOverrides="text-base"
          />
        </div>
      )}
      <div className="flex flex-row justify-self-start gap-4 w-full">
        {/* LEFT */}
        <div className="flex flex-col gap-2 w-2/5">
          {thumbnail ? (
            <Thumbnail
              imageUrl={thumbnail ?? ""}
              imageAltText={provider?.photoFileName ?? ""}
              id={provider?.photoFileId ?? ""}
              ariaLabel={provider?.photoFileName ?? ""}
              size="lg"
            />
          ) : (
            <img src={Avatar} alt="No Image Available" className="w-48 h-32" />
          )}
          <div
            className="flex flex-col gap-2 items-start w-fit ml-2 mt-2 mb-2"
            role="list"
            aria-label="Provider status badges"
          >
            {badgeItems.map((b) => (
              <div key={`${provider.id}-${b.id}`} role="listitem">
                {b.element}
              </div>
            ))}
          </div>
          <IconWithCaption
            caption={
              (provider.hasPatientWaitList
                ? publicProfileContent?.atAGlanceSubsectionPatientWaitlistText
                : provider.isAcceptingNewPatients
                  ? publicProfileContent?.atAGlanceSubsectionAcceptingPatientsText
                  : publicProfileContent?.atAGlanceSubsectionNoNewPatientsText) ??
              ""
            }
            icon="acceptingNewPatients"
            textLocation="right"
            size="md"
            fontSize="sm"
            wrap
            divStyleOverrides="-ml-6"
          />
          <div className="ml-2">
            <Typography
              variant={typographyTypes.LABEL}
              text={`${publicProfileContent?.therapistPanelLocations}:`}
            />
            <Typography variant={typographyTypes.LABEL} text={locations} />
          </div>
          <Typography
            variant={typographyTypes.NORMAL}
            text={formatPhoneNumber(
              provider.providerPhones[0]?.number ?? null,
              true,
            )}
            styleOverrides="ml-2"
          />
        </div>

        {/* RIGHT */}
        <div className="w-3/5 items-start h-fit">
          <ButtonWrapper
            onClick={() => handleViewProfileClick()}
            styleOverrides="pl-0"
          >
            <Typography
              text={`${provider.firstName} ${provider.lastName}`}
              variant={typographyTypes.HREF}
              styleOverrides="text-lg font-semibold"
            />
          </ButtonWrapper>
          <Typography
            variant={typographyTypes.LABEL}
            text={
              provider.providerDesignationDisplayIds.length > 0 && isCMSReady
                ? getSortedValuesByKeys(
                    provider.providerDesignationDisplayIds,
                    designationsMap || {},
                  ).join(", ")
                : ""
            }
          />
          <Typography
            variant={typographyTypes.NORMAL}
            text={`${truncWords(provider?.biography ?? "", MAX_BIO_LENGTH)}`}
          />
          {countWords(provider?.biography ?? "") > MAX_BIO_LENGTH && (
            <ButtonWrapper
              onClick={() => handleViewProfileClick()}
              styleOverrides="pl-0"
            >
              <Typography
                text={publicProfileContent?.therapistPanelBioReadMore}
                variant={typographyTypes.HREF}
                styleOverrides="text-sm font-medium"
              />
            </ButtonWrapper>
          )}
        </div>
      </div>
      <div>
        {provider.providerTagDisplayIds.length > 0 &&
          hasARLevelAccess &&
          isCMSReady && (
            <div className="flex flex-wrap gap-2 mt-4">
              {getSortedValuesByKeys(
                provider.providerTagDisplayIds,
                tagsMap || {},
              )
                .slice(0, TAG_LIMIT)
                .map((tag, index) => (
                  <Tag key={index} text={tag} />
                ))}
            </div>
          )}
      </div>
      {canRequestReferral &&
        practiceInfo.practiceStatus === "active" &&
        (!isNoVirtualLocationEnabled || referralLocations.length > 0) &&
        (referralLocations.length === 1 ? (
          <Button
            buttonLabel={publicProfileContent?.requestReferral}
            onClick={handleRequestReferralClick}
            roundedFull={false}
            rootStyleOverrides="w-full mt-5"
          />
        ) : (
          <div className="mt-5">
            <Select<LocationOption>
              isDisabled={false}
              placeholder={publicProfileContent?.requestReferral}
              placeholderTextColor="white"
              placeholderTextPosition="center"
              inputId="therapist-request-referral"
              label={null}
              showLabel={false}
              ariaLabel="Select a request referral location"
              onChange={handleLocationSelect}
              options={locationOptions}
              value={null}
              backgroundColor="#007DB3"
              isSearchable={false}
              borderless={false}
              menuPlacement={isLastCard ? "top" : "auto"}
              dropdownIndicatorColor="white"
            />
          </div>
        ))}
      {isRequestReferralModalOpen && selectedLocation && (
        <RequestReferralModal
          isOpen={isRequestReferralModalOpen}
          onClose={() => setIsRequestReferralModalOpen(false)}
          providerId={provider.id || ""}
          practiceId={practiceInfo.practiceId}
          isUnderSupervision={provider.isUnderSupervision}
          locationData={{
            locationId: selectedLocation.id || null,
            provider: `${provider.firstName} ${provider.lastName}`,
            practice: practiceInfo.practiceName,
            careType:
              selectedLocation.locationTypeDisplayId ===
              LocationTypeDisplayId.VIRTUAL
                ? (commonTexts?.virtual ?? "")
                : (commonTexts?.inPerson ?? ""),
            officeLocation:
              selectedLocation.locationTypeDisplayId ===
              LocationTypeDisplayId.VIRTUAL
                ? (commonTexts?.virtual ?? "")
                : `${selectedLocation.line1}, ${selectedLocation.line2 && selectedLocation.line2.length > 0 ? selectedLocation.line2 + ", " : ""}${selectedLocation.city}, ${selectedLocation.stateOrTerritory} ${selectedLocation.postalCode}`,
          }}
          hasPracticeLiabilityInsurance={
            practiceInfo.authReleaseRequired
              ? [
                  LiabilityStatusEnum.ACTIVE,
                  LiabilityStatusEnum.EXPIRING_SOON,
                ].includes(
                  getAggregateInsuranceStatus(
                    (practiceLiabilityInsurances || []).map((insurance) => ({
                      liabilityInsuranceStatusDisplayId:
                        insurance.insuranceStatus,
                    })),
                  ),
                )
              : [
                  LiabilityStatusEnum.ACTIVE,
                  LiabilityStatusEnum.EXPIRING_SOON,
                ].includes(
                  getAggregateInsuranceStatus(
                    provider.liabilityInsurances || [],
                  ),
                )
          }
          hasProviderLiabilityInsurance={[
            LiabilityStatusEnum.ACTIVE,
            LiabilityStatusEnum.EXPIRING_SOON,
          ].includes(
            getAggregateInsuranceStatus(provider.liabilityInsurances || []),
          )}
          hasProviderLicense={provider.hasLicense ?? false}
          hasProviderDisclosure={
            hasActiveDisclosure ||
            provider.providerTypeDisplayId === ProviderTypeDisplayId.MANAGED
          }
        />
      )}
    </div>
  )
}

export const TherapistContent: React.FC<TherapistContentProps> = ({
  providers,
  practiceLocations,
  practiceInfo,
  practiceLiabilityInsurances,
}) => {
  const isAffiliateRelations = useSelector(selectIsAffiliateRelations)
  const isHaUser = useSelector(selectValidatedHealthAdvocateUser)
  const hasARLevelAccess = isAffiliateRelations || isHaUser
  return (
    <SubsectionContainer gridContainerClassOverride="flex flex-col space-y-8">
      {providers
        ?.sort((a, b) => {
          if (!a.lastName) return 1
          if (!b.lastName) return -1
          return a.lastName.localeCompare(b.lastName)
        })
        .map((provider, index, arr) => (
          <TherapistCard
            key={`therapist-card-${index}`}
            provider={provider}
            hasARLevelAccess={hasARLevelAccess}
            practiceLocations={practiceLocations}
            practiceInfo={practiceInfo}
            isUnderSupervision={provider.isUnderSupervision}
            practiceLiabilityInsurances={practiceLiabilityInsurances}
            isLastCard={index === arr.length - 1}
          />
        ))}
    </SubsectionContainer>
  )
}
